import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ApiForm, Field, TextInput, useApiForm } from '../../shared/form';
import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Segmented } from '../../shared/ui/segmented';
import { createDestination, type TDestination } from '../../entities/destination';

/** The server's own default, resent because the contract marks it required. */
const TIMEOUT_MS = 30000;

type TTransport = 'http' | 'cli';

const TRANSPORTS = [
  { value: 'http', label: 'An HTTP endpoint' },
  { value: 'cli', label: 'My machine, via the CLI' },
] as const;

const schema = z
  .object({
    transport: z.enum(['http', 'cli']),
    name: z.string().trim().min(1, 'Give it a name you will recognise'),
    url: z.string().trim(),
  })
  .refine((values) => values.transport === 'cli' || /^https?:\/\/.+/.test(values.url), {
    path: ['url'],
    message: 'An http:// or https:// address',
  });

type TValues = z.infer<typeof schema>;

export const DestinationCreate = ({
  onCreated,
  label = 'New destination',
  open: opened,
  onOpenChange,
}: {
  onCreated: (destination: TDestination) => void;
  label?: string;
  /** Opened from elsewhere (a picker's footer): no button of its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [own, setOwn] = useState(false);
  const open = opened ?? own;
  const setOpen = (next: boolean) => {
    setOwn(next);
    onOpenChange?.(next);
  };
  const client = useQueryClient();

  const api = useApiForm<TValues>({
    schema,
    defaultValues: { transport: 'http', name: '', url: '' },
    onSubmit: async (values) => {
      const destination = await createDestination({
        name: values.name,
        transport: values.transport,
        config: values.transport === 'http' ? { url: values.url } : {},
        timeoutMs: TIMEOUT_MS,
      });
      await client.invalidateQueries({ queryKey: ['destinations'] });
      setOpen(false);
      onCreated(destination);
    },
  });

  const transport = api.form.watch('transport') as TTransport;

  const show = (next: boolean) => {
    if (next) api.form.reset();
    setOpen(next);
  };

  return (
    <>
      {opened === undefined && (
        <Button size="sm" onClick={() => show(true)}>
          {label}
        </Button>
      )}

      <Modal
        open={open}
        onOpenChange={show}
        width="md"
        title="New destination"
        description="Somewhere to deliver to. A flow is what sends anything here."
        submit={{
          label: 'Create destination',
          onSubmit: () => void api.submit(),
          disabled: api.form.formState.isSubmitting,
          waiting: api.form.formState.isSubmitting,
          waitingLabel: 'Creating',
        }}
      >
        <ApiForm api={api}>
          <Segmented
            fill
            aria-label="Where it delivers"
            segments={TRANSPORTS}
            value={transport}
            onChange={(next) => api.form.setValue('transport', next)}
          />

          <Field<TValues> name="name" label="Name">
            <TextInput
              placeholder={transport === 'cli' ? 'My laptop' : 'Billing worker'}
              autoFocus
            />
          </Field>

          {transport === 'http' ? (
            <>
              <Field<TValues> name="url" label="URL">
                <TextInput mono placeholder="https://billing.rawnodes.dev/hooks" />
              </Field>
              <Note>
                Method, credential, signing, timeout, rate limit and headers are on the
                destination’s own page. Until you set them it gets a POST, unsigned, 30 s timeout,
                no limit.
              </Note>
            </>
          ) : (
            <Note>
              Events wait here until you run <span className="font-mono">hookpipe listen</span> on
              your machine; the CLI then forwards each one to your local server. Its page shows
              whether an agent is connected.
            </Note>
          )}

          {/* The real button is in the footer, outside the form; without this one Enter does nothing. */}
          <button type="submit" className="hidden" aria-hidden />
        </ApiForm>
      </Modal>
    </>
  );
};
